import type { FC } from 'react'
import { Box, MenuItem, Select, Typography } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { required } from '~/shared/lib/required'

/** One plan year on offer; each tab's generated `policy_configurations` entry is assignable. */
interface PlanYear {
  id: number
  plan_year_label: string
  plan_year_date_range: string
}

/** The year as staff read it, or a fallback for a configuration the backend could not date. */
const planYearText = (planYear: PlanYear): string =>
  [planYear.plan_year_label, planYear.plan_year_date_range].filter(Boolean).join(' · ') ||
  'Undated plan year'

interface PlanYearPickerProps {
  planYears: PlanYear[]
  selectedPlanYearId: number | undefined
  onChange: (planYearId: number) => void
  /** Suffixes the label id and the test id, so two pickers on one screen stay distinct. */
  idPrefix: string
}

/**
 * The plan year a Health Plan tab is reading from; one year renders as text, not a dead control.
 */
export const PlanYearPicker: FC<PlanYearPickerProps> = ({
  planYears,
  selectedPlanYearId,
  onChange,
  idPrefix,
}) => {
  const labelId = `${idPrefix}-plan-year-label`

  return (
    <Box>
      <Typography
        id={labelId}
        variant="body2"
        sx={{
          mb: 1,
          color: 'text.secondary',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
      >
        Plan year
      </Typography>

      {planYears.length === 1 ? (
        // At the picker's height, so what follows sits in the same place either way.
        <Typography
          data-testid={`${idPrefix}-plan-year`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            minHeight: 42,
            fontSize: '1.4rem',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {planYearText(required(planYears[0], 'the one plan year'))}
        </Typography>
      ) : (
        <Select
          labelId={labelId}
          value={selectedPlanYearId ?? ''}
          onChange={event => onChange(Number(event.target.value))}
          IconComponent={KeyboardArrowDownIcon}
          size="small"
          data-testid={`${idPrefix}-plan-year`}
          sx={{
            minWidth: 320,
            maxWidth: '100%',
            bgcolor: 'common.white',
            borderRadius: '8px',
            fontSize: '1.4rem',
            fontWeight: 600,
            '& .MuiSelect-select': { py: 1.25, pl: 2 },
          }}
        >
          {planYears.map(planYear => (
            <MenuItem key={planYear.id} value={planYear.id} sx={{ fontSize: '1.4rem' }}>
              {planYearText(planYear)}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  )
}
